import React, { useEffect, useState } from "react";
import axios from "axios";
import SEO from "@components/seo";
import { useLocation } from "@reach/router";
import Layout from "@components/layout";
import PageSlider from "@components/flows/shared/pageSlider";
import SliderPageLogo from "@components/flows/shared/sliderPageLogo";
import FlowStepBusinessDetails from "@components/flows/shared/flowStepBusinessDetails";
import FlowStepBusinessSize from "@components/flows/shared/flowStepBusinessSize";
import SingleProviderWarning from "@components/flows/shared/singleProviderWarning";
import * as c from "./trial.module.scss";
import FlowStepPhone from "@components/flows/shared/flowStepPhone";
import FlowStepPhoneVerify from "@components/flows/shared/flowStepPhoneVerify";
import FlowStepMobileAccount from "@components/flows/shared/flowStepMobileAccount";
import FlowStepUserInfo from "@components/flows/shared/flowStepUserInfo";
import TrialStepCreatingAccount from "@components/flows/trial/trialStepCreatingAccount";
import FlowStepBusinessServices from "@components/flows/shared/flowStepBusinessServices";
import { getVisitInfoVerbose } from "@utils/visitTracking";
import { identify, trackEvent, trackBingConversion } from "@utils/analytics";
import useUserAgent from "@hooks/useUserAgent";

export default function Trial() {
  const [step, setStep] = useState("businessServices");
  const [data, setData] = useState({});
  const location = useLocation();

  const hasDevFlag = location.search != null && location.search.indexOf("dev=true") >= 0;
  const hasProdFlag = location.search != null && location.search.indexOf("prod=true") >= 0;
  const { isMobile } = useUserAgent();

  const isDev =
    (process.env.NODE_ENV === "production" && hasDevFlag) || (process.env.NODE_ENV !== "production" && !hasProdFlag);

  const doApiSignup = !isDev || (location.search != null && location.search.indexOf("doapisignup=true") >= 0);

  const createSubmitHandler = (next) => {
    return (partialData) => {
      const mergedData = {
        ...data,
        ...partialData,
      };

      setData(mergedData);

      let nextStep;
      if (typeof next === "function") {
        nextStep = next(mergedData);
      } else {
        nextStep = next;
      }

      setStep(nextStep);
      setLogoHidden(false);
    };
  };

  let pageEl;
  switch (step) {
    case "businessServices": {
      pageEl = (
        <FlowStepBusinessServices
          stepNo={1}
          totalSteps={5}
          forTrial={true}
          onDataSubmit={createSubmitHandler("businessSize")}
        />
      );
      break;
    }

    case "businessSize": {
      pageEl = (
        <FlowStepBusinessSize
          stepNo={2}
          totalSteps={5}
          onDataSubmit={createSubmitHandler((data) => {
            if (data.employees === "1") {
              return "singleProvider";
            }

            return "userInfo";
          })}
        />
      );
      break;
    }

    case "singleProvider": {
      pageEl = (
        <SingleProviderWarning
          forTrial={true}
          toggleLabel="I understand and still want to try Mangomint"
          buttonLabel="Continue"
          onContinue={() => {
            setStep("userInfo");
            setLogoHidden(false);
          }}
        />
      );
      break;
    }

    case "userInfo": {
      pageEl = (
        <FlowStepUserInfo
          stepNo={3}
          totalSteps={5}
          forTrial={true}
          onDataSubmit={createSubmitHandler("businessDetails")}
          isDev={isDev}
          doApiSignup={doApiSignup}
          isMobile={isMobile}
        />
      );
      break;
    }

    case "businessDetails": {
      pageEl = (
        <FlowStepBusinessDetails
          stepNo={4}
          totalSteps={5}
          forTrial={true}
          onDataSubmit={createSubmitHandler("phone")}
        />
      );
      break;
    }

    case "phone": {
      pageEl = (
        <FlowStepPhone stepNo={5} totalSteps={5} forTrial={true} onDataSubmit={createSubmitHandler("phoneVerify")} />
      );
      break;
    }

    case "phoneVerify": {
      pageEl = (
        <FlowStepPhoneVerify
          stepNo={5}
          totalSteps={5}
          forTrial={true}
          phone={data.phone}
          onVerified={() => {
            setStep("creatingAccount");
            setLogoHidden(false);
          }}
          onTryAgain={() => {
            setStep("phone");
            setLogoHidden(false);
          }}
        />
      );
      break;
    }

    case "creatingAccount": {
      pageEl = (
        <TrialStepCreatingAccount
          data={data}
          isDev={isDev}
          doApiSignup={doApiSignup}
          isMobile={isMobile}
          forTrial={true}
          onDataSubmit={createSubmitHandler("authUrl")}
          onMobileComplete={() => {
            setStep("mobileSignup");
            setLogoHidden(false);
          }}
        />
      );
      break;
    }

    case "mobileSignup": {
      pageEl = <FlowStepMobileAccount data={data} />;
      break;
    }
    default:
      break;
  }

  const currentPage = {
    name: step,
    pageEl,
  };

  useEffect(() => {
    if (isDev) {
      return;
    }

    if (step === "creatingAccount") {
      // send data to Basin just so we have it as a backup
      // this does NOT actually create the trial account
      const formUrl = "https://usebasin.com/f/aa22e9e40d2c";

      const options = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      const { ...dataWithoutPassword } = data;

      const dataWithFirstVisit = {
        ...dataWithoutPassword,
        ...getVisitInfoVerbose("first"),
        ...getVisitInfoVerbose("current"),
      };

      axios.post(formUrl, dataWithFirstVisit, options).catch((e) => {
        console.error(e);
      });

      identify({
        email: data["email"],
        firstName: data["first-name"],
        lastName: data["last-name"],
        phone: data["phone"],
        company: data["business-name"],
        website: data["website"],
        businessSize: data["employees"],
        businessServiceTypes: data["business-services"],
      });
    }

    // Analytics tracking
    let name;
    switch (step) {
      case "businessServices":
        name = "Business Services";
        break;
      case "businessSize":
        name = "Business Size";
        identify({
          businessServiceTypes: data["business-services"],
        });
        break;
      case "singleProvider":
        name = "Single Provider";
        break;
      case "userInfo":
        name = "User Info";
        identify({
          businessSize: data["employees"],
          businessServiceTypes: data["business-services"],
        });
        break;
      case "businessDetails":
        name = "Business Details";
        identify({
          email: data["email"],
          firstName: data["first-name"],
          lastName: data["last-name"],
          businessSize: data["employees"],
          businessServiceTypes: data["business-services"],
        });
        break;
      case "phone":
        name = "Phone";
        break;
      case "phoneVerify":
        name = "Phone Verify";
        identify({
          email: data["email"],
          firstName: data["first-name"],
          lastName: data["last-name"],
          phone: data["phone"],
          company: data["business-name"],
          website: data["website"],
          businessSize: data["employees"],
          businessServiceTypes: data["business-services"],
        });
        break;
      case "creatingAccount":
        name = "Creating Account";
        break;
      case "mobileSignup":
        name = "Mobile Signup";
        break;
      default:
        name = "Unknown";
        break;
    }

    if (window.analytics) {
      trackEvent("Start Trial: " + name);

      if (name === "Creating Account") {
        trackBingConversion("trial_signup");
      }
    }
  }, [step, data, isDev]);

  const [isLogoHidden, setLogoHidden] = useState(false);

  const handleScroll = (e) => {
    setLogoHidden(e.target.scrollTop > 0);
  };

  return (
    <Layout noNavFooter={true}>
      <SEO
        title="Try for free"
        description="Try our all-in-one salon and spa software free for 21 days! Reduce costs and streamline operations. Booking, POS, client and staff management, retail inventory."
      />
      <SliderPageLogo isHidden={isLogoHidden} />
      <PageSlider currentPage={currentPage} onScroll={handleScroll} />
    </Layout>
  );
}

export function Head() {
  return (
    <>
      <body className={c.body} />
    </>
  );
}
